import { useState, useEffect, useRef, useCallback } from 'react';
import { Palette, Upload, Type, Save, AlertCircle, Trash2, RotateCcw, Image, X, Check } from '../../icons/wp-icons';
import { brandingApi } from '../../api/brandingApi';
import { triggerBrandingUpdate } from '../../hooks/useBranding';

// Font-Kategorien für Fallback
const FONT_CATEGORIES = {
  'Instrument Serif': 'serif',
  'Inter': 'sans-serif',
  'Roboto': 'sans-serif',
  'Open Sans': 'sans-serif',
  'Lato': 'sans-serif',
  'Montserrat': 'sans-serif',
  'Poppins': 'sans-serif',
  'Playfair Display': 'serif',
  'Merriweather': 'serif'
};

function DesignSettings({ readOnly = false }) {
  // State
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Form State
  const [headingFont, setHeadingFont] = useState('Montserrat');
  const [accentColor, setAccentColor] = useState('#378ab2');
  const [logoPreview, setLogoPreview] = useState(null);

  // Upload State
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Verfügbare Optionen
  const [availableFonts, setAvailableFonts] = useState([]);
  const [presetColors, setPresetColors] = useState([]);

  useEffect(() => {
    loadSettings();
  }, []);

  // Google Fonts laden wenn sich die Schriftart ändert
  useEffect(() => {
    if (headingFont && headingFont !== 'Montserrat') {
      const fontName = headingFont.replace(/ /g, '+');
      const linkId = 'google-font-preview';

      // Existierenden Link entfernen
      const existingLink = document.getElementById(linkId);
      if (existingLink) {
        existingLink.remove();
      }

      // Neuen Link erstellen
      const link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${fontName}:wght@400;600;700&display=swap`;
      document.head.appendChild(link);
    }
  }, [headingFont]);

  const loadSettings = async () => {
    try {
      const data = await brandingApi.get();
      setSettings(data);
      setHeadingFont(data.headingFont || 'Montserrat');
      setAccentColor(data.accentColor || '#378ab2');
      setLogoPreview(data.logoPath || null);
      setAvailableFonts(data.availableFonts || []);
      setPresetColors(data.presetColors || []);
    } catch (err) {
      console.error('Fehler beim Laden der Design-Einstellungen:', err);
      setError('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);

    try {
      await brandingApi.update({
        headingFont,
        accentColor
      });

      // CSS-Variablen sofort aktualisieren - Akzentfarbe
      document.documentElement.style.setProperty('--primary', accentColor);
      document.documentElement.style.setProperty('--primary-dark', adjustColor(accentColor, -15));
      document.documentElement.style.setProperty('--primary-light', adjustColor(accentColor, 90, 0.1));

      // Akzentfarbe persistieren und Branding-Update auslösen,
      // damit das (ggf. gefärbte) Standard-Logo neu berechnet wird
      localStorage.setItem('branding-accent', accentColor);
      triggerBrandingUpdate();

      // CSS-Variable für Schriftart aktualisieren
      const fontCategory = FONT_CATEGORIES[headingFont] || 'sans-serif';
      document.documentElement.style.setProperty('--heading-font', `'${headingFont}', ${fontCategory}`);

      // Google Font laden falls nötig
      if (headingFont !== 'Montserrat') {
        const fontName = headingFont.replace(/ /g, '+');
        const existingLink = document.getElementById('branding-font');
        if (existingLink) {
          existingLink.remove();
        }
        const link = document.createElement('link');
        link.id = 'branding-font';
        link.rel = 'stylesheet';
        link.href = `https://fonts.googleapis.com/css2?family=${fontName}:wght@400;500;600;700&display=swap`;
        document.head.appendChild(link);
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      setError('Fehler beim Speichern der Einstellungen');
    } finally {
      setSaving(false);
    }
  };

  // Datei-Upload Handler
  const handleFileSelect = useCallback(async (file) => {
    if (!file) return;

    // Validierung
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setError('Ungültiges Dateiformat. Erlaubt: PNG, JPG, SVG');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('Datei zu groß. Maximum: 2 MB');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const result = await brandingApi.uploadLogo(file);
      setLogoPreview(result.logoPath);
      // LocalStorage und Header aktualisieren
      localStorage.setItem('branding-logo', result.logoPath);
      triggerBrandingUpdate();
    } catch (err) {
      console.error('Fehler beim Hochladen:', err);
      setError(err.response?.data?.error || 'Fehler beim Hochladen des Logos');
    } finally {
      setUploading(false);
    }
  }, []);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, [handleFileSelect]);

  const handleDeleteLogo = async () => {
    if (!confirm('Logo wirklich entfernen?')) return;

    try {
      await brandingApi.deleteLogo();
      setLogoPreview(null);
      // LocalStorage und Header aktualisieren
      localStorage.removeItem('branding-logo');
      triggerBrandingUpdate();
    } catch (err) {
      console.error('Fehler beim Entfernen:', err);
      setError('Fehler beim Entfernen des Logos');
    }
  };

  const handleReset = async () => {
    if (!confirm('Alle Design-Einstellungen auf Standard zurücksetzen?')) return;

    try {
      const result = await brandingApi.reset();
      setHeadingFont(result.headingFont);
      setAccentColor(result.accentColor);
      setLogoPreview(null);

      // CSS-Variablen zurücksetzen
      document.documentElement.style.setProperty('--primary', '#378ab2');
      document.documentElement.style.setProperty('--primary-dark', '#2a6d8e');
      document.documentElement.style.setProperty('--primary-light', '#e8f4f9');
      document.documentElement.style.setProperty('--heading-font', "'Montserrat', sans-serif");

      // Standardakzent persistieren, damit das Standard-Logo wieder unverändert geladen wird
      localStorage.setItem('branding-accent', '#378ab2');

      // Google Font Link entfernen
      const existingLink = document.getElementById('branding-font');
      if (existingLink) {
        existingLink.remove();
      }

      // LocalStorage und Header aktualisieren
      localStorage.removeItem('branding-logo');
      triggerBrandingUpdate();
    } catch (err) {
      console.error('Fehler beim Zurücksetzen:', err);
      setError('Fehler beim Zurücksetzen');
    }
  };

  // Hilfsfunktion zum Anpassen der Farbe
  const adjustColor = (hex, percent, opacity = 1) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const r = Math.min(255, Math.max(0, (num >> 16) + percent));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + percent));
    const b = Math.min(255, Math.max(0, (num & 0x0000FF) + percent));

    if (opacity < 1) {
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    return `#${(1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1)}`;
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-card shadow-card p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-light rounded-lg">
            <Palette className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Design</h2>
            <p className="text-sm text-text-muted">Logo, Schriftart und Akzentfarbe anpassen</p>
          </div>
        </div>
        {!readOnly && (
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-3 py-2 text-sm text-text-muted hover:text-text hover:bg-gray-100 rounded-button transition"
          >
            <RotateCcw className="w-4 h-4" />
            <span className="hidden sm:inline">Zurücksetzen</span>
          </button>
        )}
      </div>

      <div className="space-y-8">
        {/* LOGO UPLOAD - nur anzeigen wenn nicht readOnly */}
        <div>
          <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
            <Image className="w-4 h-4" />
            Firmenlogo
          </h3>

          <div className="flex flex-col sm:flex-row gap-4">
            {/* Aktuelle Logo-Vorschau */}
            {logoPreview && (
              <div className="relative w-32 h-32 bg-gray-100 rounded-lg border border-border flex items-center justify-center overflow-hidden">
                <img
                  src={`${import.meta.env.VITE_API_URL?.replace('/api', '')}${logoPreview}`}
                  alt="Aktuelles Logo"
                  className="max-w-full max-h-full object-contain"
                />
                {!readOnly && (
                  <button
                    onClick={handleDeleteLogo}
                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}

            {/* Upload-Bereich - nur wenn nicht readOnly */}
            {!readOnly && (
            <div
              className={`flex-1 border-2 border-dashed rounded-lg p-6 text-center transition cursor-pointer ${
                dragActive
                  ? 'border-primary bg-primary-light'
                  : 'border-border hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                onChange={(e) => handleFileSelect(e.target.files?.[0])}
                className="hidden"
              />

              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm text-text-muted">Hochladen...</span>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 mx-auto text-text-muted mb-2" />
                  <p className="text-sm font-medium">Logo hierher ziehen oder klicken</p>
                  <p className="text-xs text-text-muted mt-1">PNG, JPG oder SVG, max. 2 MB</p>
                </>
              )}
            </div>
            )}
          </div>

          <p className="text-xs text-text-muted mt-2">
            Das Logo erscheint in der Sidebar, auf der Login-Seite und auf Rechnungen/PDFs.
          </p>
        </div>

        {/* SCHRIFTART */}
        <div>
          <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
            <Type className="w-4 h-4" />
            Schriftart für Überschriften
          </h3>

          <select
            value={headingFont}
            onChange={(e) => !readOnly && setHeadingFont(e.target.value)}
            disabled={readOnly}
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            {availableFonts.map((font) => (
              <option key={font.name} value={font.name}>
                {font.name} {font.default ? '(Standard)' : ''} - {font.category}
              </option>
            ))}
          </select>

          {/* Live-Vorschau */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-border">
            <p className="text-xs text-text-muted mb-2">Vorschau:</p>
            <div
              className="text-2xl font-semibold"
              style={{ fontFamily: `'${headingFont}', ${FONT_CATEGORIES[headingFont] || 'sans-serif'}` }}
            >
              Dashboard
            </div>
            <div
              className="text-xl font-semibold mt-2"
              style={{ fontFamily: `'${headingFont}', ${FONT_CATEGORIES[headingFont] || 'sans-serif'}` }}
            >
              Rechnungen & Kostenvoranschläge
            </div>
          </div>
        </div>

        {/* AKZENTFARBE */}
        <div>
          <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
            <Palette className="w-4 h-4" />
            Akzentfarbe
          </h3>

          {/* Preset-Farben */}
          <div className="flex flex-wrap gap-2 mb-4">
            {presetColors.map((color) => (
              <button
                key={color.hex}
                onClick={() => !readOnly && setAccentColor(color.hex)}
                disabled={readOnly}
                className={`w-10 h-10 rounded-lg border-2 transition ${
                  accentColor === color.hex
                    ? 'border-gray-900 ring-2 ring-offset-2 ring-gray-900'
                    : 'border-transparent hover:border-gray-300'
                } ${readOnly ? 'cursor-not-allowed opacity-60' : ''}`}
                style={{ backgroundColor: color.hex }}
                title={color.name}
              />
            ))}
          </div>

          {/* Color Picker + HEX Input */}
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={accentColor}
              onChange={(e) => !readOnly && setAccentColor(e.target.value)}
              disabled={readOnly}
              className={`w-12 h-12 rounded border border-border ${readOnly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
            />
            <div className="flex-1">
              <label className="block text-xs text-text-muted mb-1">HEX-Code</label>
              <input
                type="text"
                value={accentColor}
                onChange={(e) => {
                  if (readOnly) return;
                  const val = e.target.value;
                  if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                    setAccentColor(val);
                  }
                }}
                disabled={readOnly}
                placeholder="#057867"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          {/* Live-Vorschau */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-border">
            <p className="text-xs text-text-muted mb-3">Vorschau:</p>
            <div className="flex flex-wrap gap-3">
              <button
                className="px-4 py-2 text-white rounded-button font-medium transition"
                style={{ backgroundColor: accentColor }}
              >
                Primärer Button
              </button>
              <button
                className="px-4 py-2 border rounded-button font-medium transition"
                style={{ borderColor: accentColor, color: accentColor }}
              >
                Sekundärer Button
              </button>
              <span
                className="px-3 py-1 rounded-full text-sm font-medium"
                style={{
                  backgroundColor: adjustColor(accentColor, 90, 0.2),
                  color: accentColor
                }}
              >
                Badge
              </span>
            </div>
          </div>

          <p className="text-xs text-text-muted mt-2">
            Die Akzentfarbe wird für Buttons, Links, aktive Menüpunkte und Highlights verwendet.
          </p>
        </div>

        {/* FEHLER */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {/* ERFOLG */}
        {saved && (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
            <Check className="w-4 h-4 shrink-0" />
            Design-Einstellungen erfolgreich gespeichert
          </div>
        )}

        {/* SPEICHERN */}
        {!readOnly && (
          <div className="pt-4 border-t border-border">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Einstellungen speichern
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default DesignSettings;
